import { now, offsetDateTimeToMs } from "@freightsimple/time/helpers";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { AuthorizationApi2 } from "@/Apis/AuthorizationApi2";
import auth0 from "auth0-js";
import { apiServerUrl, auth0Creds, cookieDomain, isLocal } from "@/Environment";
import moment from "moment";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store";
import { isBlank } from "@freightsimple/shared/Helpers/isNotBlank";
import { getConfig } from "@/Apis/getConfig";
import type { UserInfo } from "@freightsimple/generated-dashboard-openapi-client/models/UserInfo";
import axios from "axios";
import {
  isUserV0,
  migrateUserV0ToV1,
  type UserV1,
} from "@/Helpers/authenticationUserMigration";
import { identifyPostHogUser } from "@/Integrations/identifyPostHogUser";
import { resetPostHogUser } from "@/Integrations/resetPostHogUser";

export type User = UserV1;

// Be careful when adding to the slice, as they might be serialized into local storage. So new field might be missing.
export interface AuthenticationSliceType {
  accessToken?: string | undefined;
  expiresAt?: number | undefined;
  user?: User | undefined;
  redirectUrl?: string | undefined;
  // Whose expired session saved redirectUrl. Anyone else signing in on this
  // browser goes home instead of to that person's page.
  redirectUserId?: string | undefined;
  // Set when the session ended on its own rather than by Sign Out, so the
  // sign-in screen can say why the customer is there.
  sessionExpired?: boolean | undefined;
  emailAddressForReset: string;
  userVersion?: number; // 0 = migrated from UserV0 (needs refresh), 1 = fresh from server
}

// A session counts as over this long before its token expires, so a request
// never leaves with a token that dies in flight.
export const SESSION_EXPIRY_BUFFER_MS = 60_000;

export function isAuthenticationDisabled(): boolean {
  return Boolean(import.meta.env["VITE_DISABLE_AUTHENTICATION"]) && isLocal();
}

export function emptyAuthenticationSlice(): AuthenticationSliceType {
  return {
    emailAddressForReset: "",
  };
}

/** Where sign-in should go: the saved page, unless an expired session saved it
 *  for a different user. */
export function signInDestination(
  redirectUrl: string | undefined,
  redirectUserId: string | undefined,
  signedInUserId: string | undefined,
): string {
  if (redirectUrl === undefined) {
    return "/";
  }
  if (redirectUserId !== undefined && redirectUserId !== signedInUserId) {
    return "/";
  }
  return redirectUrl;
}

/** The session as last written to localStorage, which every tab shares. */
export function readStoredAuthentication(): AuthenticationSliceType {
  return initialState(true);
}

function initialState(allowReload: boolean): AuthenticationSliceType {
  const serializedState = localStorage.getItem("authentication");
  if (serializedState && allowReload) {
    try {
      const parsed = JSON.parse(serializedState);

      // Check if we have old UserV0 format and migrate it
      if (parsed.user && isUserV0(parsed.user)) {
        console.info(
          "Detected UserV0 format in localStorage. Migrating to UserV1 with placeholder data.",
        );
        return {
          ...parsed,
          user: migrateUserV0ToV1(parsed.user),
          userVersion: 0, // Mark as needing refresh from server
        };
      }

      return parsed;
    } catch (e) {
      console.warn("Failed to parse serialized authenticationSlice", {
        e,
      });
      return emptyAuthenticationSlice();
    }
  } else {
    return emptyAuthenticationSlice();
  }
}

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState: initialState(true),
  reducers: {
    storeAuthentication(
      state,
      action: PayloadAction<{
        accessToken: string;
        expiresAt?: number | undefined;
        user: User;
      }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.expiresAt = action.payload.expiresAt;
      state.user = action.payload.user;
      state.userVersion = 1; // Mark as fresh from server
      // Sign-in reads it before storing, so it's used up by now. Kept, it
      // would send every later sign-in back to the same old page.
      state.redirectUrl = undefined;
      state.redirectUserId = undefined;
      state.sessionExpired = undefined;
    },

    clearStoredAuthentication() {
      return emptyAuthenticationSlice();
    },

    // Unlike Sign Out, an expired session keeps the page the customer was on,
    // so signing back in as the same user returns them to it.
    expireSession(
      _state,
      action: PayloadAction<{
        redirectUrl: string;
        userId: string | undefined;
      }>,
    ) {
      return {
        ...emptyAuthenticationSlice(),
        redirectUrl: action.payload.redirectUrl,
        redirectUserId: action.payload.userId,
        sessionExpired: true,
      };
    },

    // Another tab signed out or in; this tab follows what it stored.
    adoptStoredAuthentication(
      _state,
      action: PayloadAction<AuthenticationSliceType>,
    ) {
      return action.payload;
    },

    // A page asking for sign-in, not an expired session, so whoever signs
    // in next may go there.
    setRedirectUrl(state, action: PayloadAction<string>) {
      state.redirectUrl = action.payload;
      state.redirectUserId = undefined;
    },

    setEmailAddressForReset(state, action: PayloadAction<string>) {
      state.emailAddressForReset = action.payload;
    },
  },
});

export const {
  storeAuthentication,
  clearStoredAuthentication,
  expireSession,
  adoptStoredAuthentication,
  setRedirectUrl,
  setEmailAddressForReset,
} = authenticationSlice.actions;

export const writeAuthenticationSliceToLocalStorage = (
  state: AuthenticationSliceType,
) => {
  localStorage.setItem("authentication", JSON.stringify(state));
};

interface UseAuthenticationResult {
  // Are we currently authed? Doesn't double-check with auth0 though
  isAuthenticated: boolean;
  // Deeper check - checks with auth0 so we can be really confident
  checkAuthenticated: () => Promise<boolean>;

  accessToken: string;
  user: User | undefined;
  // Resolves to the signed-in user's id, or undefined if it didn't stick.
  authenticate: (
    accessToken: string,
    expiresAt: number | undefined,
  ) => Promise<string | undefined>;
  refreshAuthentication: () => Promise<void>;
  signOut: () => void;
  redirectUrl: string | undefined;
  redirectUserId: string | undefined;
}

function useFakeAuthentication(): UseAuthenticationResult {
  const overrideCompanyId = import.meta.env[
    "VITE_DISABLE_AUTHENTICATION_COMPANY_ID"
  ];
  if (isBlank(overrideCompanyId) && isAuthenticationDisabled()) {
    throw new Error(
      `You must set VITE_DISABLE_AUTHENTICATION_COMPANY_ID env var to do override in dashboard`,
    );
  }
  const user: User = {
    // We're faking it so you look like you're attached
    email: import.meta.env["VITE_DISABLE_AUTHENTICATION_ADMIN_EMAIL"],
    userId: "fake-user-id",
    name: "Fake Person",
    companyName: "Attached Company",
    companyId: import.meta.env["VITE_DISABLE_AUTHENTICATION_COMPANY_ID"],
    company: {
      companyName: "Attached Company",
      companyId: import.meta.env["VITE_DISABLE_AUTHENTICATION_COMPANY_ID"],
      isCurrentCompany: true,
      currency: "CAD",
    },
    linkedCompanies: [],
    isLinkedCompany: false,
    phone: "1234567890",
    phoneExtension: "",
  };
  return {
    isAuthenticated: true,
    checkAuthenticated: async function () {
      return true;
    },
    accessToken: "fake-access-token",
    user: user,
    authenticate: async function (
      _accessToken: string,
      _expiresAt: number | undefined,
    ) {
      return user.userId;
    },
    refreshAuthentication: async function () {},
    signOut: function () {},
    redirectUrl: undefined,
    redirectUserId: undefined,
  };
}

function useRealAuthentication(): UseAuthenticationResult {
  const [, setCookie, removeCookie] = useCookies(["fsSignedIn"]);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => {
    const { accessToken, expiresAt } = state.authentication;

    if (!accessToken) return false;

    // If we don't have expiration time yet (old tokens in localStorage),
    // optimistically assume authenticated - useSessionExpiry checks it with
    // the server on load
    if (!expiresAt) return true;

    const nowMs = offsetDateTimeToMs(now());
    return nowMs < expiresAt - SESSION_EXPIRY_BUFFER_MS;
  });

  const accessToken =
    useSelector((state: RootState) => state.authentication.accessToken) ?? "";

  const expiresAt = useSelector(
    (state: RootState) => state.authentication.expiresAt,
  );

  const redirectUrl = useSelector((state: RootState) => {
    return state.authentication.redirectUrl;
  });

  const redirectUserId = useSelector(
    (state: RootState) => state.authentication.redirectUserId,
  );

  const user = useSelector((state: RootState) => state.authentication.user);

  if (isAuthenticated && !accessToken) {
    throw new Error("Authenticated, but missing accessToken");
  }

  if (isAuthenticated && !user) {
    throw new Error("Authenticated, but missing user");
  }

  const signOut = () => {
    const auth = new auth0.WebAuth({
      domain: auth0Creds().domain,
      clientID: auth0Creds().clientId,
      scope: "openid profile email",
    });

    removeCookie("fsSignedIn", {
      domain: cookieDomain(),
    });

    resetPostHogUser();
    dispatch(clearStoredAuthentication());

    auth.logout({});
  };

  const authenticate = async (
    accessToken: string,
    expiresAt: number | undefined,
  ): Promise<string | undefined> => {
    // Do not cache - we alwasy want the latest user info
    const options = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },

      validateStatus: () => true,
    };

    const response = await axios.get(
      `${getConfig().basePath}/users/user-info`,
      options,
    );

    if (response.status !== 200) return undefined;

    const userInfo = response.data as UserInfo;

    // When we sign in, set a cookie so the marketing pages can pick up if we are logged in
    setCookie("fsSignedIn", true, {
      expires: moment().add(1, "year").toDate(),
      domain: cookieDomain(),
      secure: true,
    });

    dispatch(
      storeAuthentication({
        accessToken,
        expiresAt,
        user: { ...userInfo, ...userInfo.company },
      }),
    );

    identifyPostHogUser(
      userInfo.userId,
      userInfo.email,
      userInfo.company.companyId,
      userInfo.company.companyName,
    );

    return userInfo.userId;
  };

  // Do a deep check on the token to make sure it's still valid
  async function checkAuthenticated(): Promise<boolean> {
    try {
      if (!isAuthenticated) {
        return false;
      }

      const status = await new AuthorizationApi2(
        apiServerUrl(),
        accessToken,
      ).postAuthorizationVerifyToken();

      return status === 200;
    } catch (e) {
      console.error(`checkAuthenticated - ${e}`);
      return false;
    }
  }

  // Same token, so the same expiry. Dropping it would leave the session with
  // no known end, noticed only once the server refuses a request.
  const refreshAuthentication = async () => {
    await authenticate(accessToken, expiresAt);
  };

  return {
    // Are we currently authed? Doesn't double-check with auth0 though
    isAuthenticated,
    // Deeper check - checks with auth0 so we can be really confident
    checkAuthenticated,

    accessToken,
    user,
    authenticate,
    refreshAuthentication,
    signOut,
    redirectUrl,
    redirectUserId,
  };
}

export default authenticationSlice.reducer;

export function useAuthentication(): UseAuthenticationResult {
  const realAuthentication = useRealAuthentication();
  const fakeAuthentication = useFakeAuthentication();

  if (isAuthenticationDisabled()) {
    return fakeAuthentication;
  } else {
    return realAuthentication;
  }
}
